import { Database } from 'lucide-react';
import type { LeadCarteira } from '@/hooks/useLeadsCarteira';

const INTERNOS = new Set([
  'ura_acao_id', 'ura_campanha_id', 'ura_digitou', 'ura_em', 'origem_mailing',
  'cpf', 'doc_cpf', 'cnpj', 'nome', 'razao_social', 'tipo_pessoa', 'tipo_lead',
  'telefone', 'telefone_2', 'telefone_3', 'telefone_4', 'telefone_titular',
  'email', 'email_receita', 'email_atendimento',
]);

const ROTULOS: Record<string, string> = {
  algar_status: 'Status na Algar',
  algar_substatus: 'Substatus',
  algar_motivos: 'Motivos',
  algar_criacao: 'Criado na Algar',
  algar_canal: 'Canal',
  algar_layout_atual: 'Layout atual',
  publico_ura: 'Público da URA',
  operadora: 'Operadora atual',
  cobertura_fibra: 'Cobertura de fibra',
  situacao_cadastral: 'Situação na Receita',
  cnae_principal: 'CNAE principal',
  natureza_juridica: 'Natureza jurídica',
  numero_endereco: 'Número',
  endereco_empresa: 'Endereço',
  nome_proprietario: 'Proprietário',
  empresa_telefone: 'Telefone da empresa',
  empresa_telefone_2: 'Telefone da empresa 2',
  valor_fatura_atual: 'Valor da fatura',
  plano_atual: 'Plano atual',
  conta_operadora: 'Conta na operadora',
};

function rotular(chave: string) {
  if (ROTULOS[chave]) return ROTULOS[chave];
  const limpo = chave.replace(/_/g, ' ').trim();
  return limpo.charAt(0).toUpperCase() + limpo.slice(1);
}

function textoDoValor(valor: unknown): string | null {
  if (valor === null || valor === undefined) return null;
  if (typeof valor === 'boolean') return valor ? 'Sim' : 'Não';
  if (Array.isArray(valor)) return valor.length ? `${valor.length} registro(s)` : null;
  if (typeof valor === 'object') return null;
  const texto = String(valor).trim();
  return texto && texto !== '-' ? texto : null;
}

/**
 * O que a lista de origem trouxe sobre esta pessoa — seja OI, ALGAR ou qualquer
 * estratégia nova. A URA é a porta, não a origem: sem isto o consultor vê o lead
 * e não sabe o que o cliente tem. Não há lista de campos chumbada: mostra o que
 * veio, com o rótulo que der, para estratégia nova aparecer sozinha.
 */
export function LeadDadosDaLista({ lead }: { lead: LeadCarteira }) {
  const dados = (lead.dados_discadora as Record<string, unknown> | null) || null;
  if (!dados) return null;

  const origem = String(dados.origem_mailing || '').trim().replace(/_/g, ' ').toUpperCase();
  const pares = Object.entries(dados)
    .filter(([chave]) => !INTERNOS.has(chave))
    .map(([chave, valor]) => [rotular(chave), textoDoValor(valor)] as const)
    .filter(([, texto]) => texto !== null)
    .sort((a, b) => a[0].localeCompare(b[0], 'pt-BR'));

  if (!pares.length) return null;

  return (
    <section className="overflow-hidden rounded-xl border border-border bg-card">
      <div className="flex min-h-[34px] items-center gap-2 border-b bg-[#FBFAF9] px-3.5 py-1.5 dark:bg-white/[0.02]">
        <h3 className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-card-foreground">
          <Database className="h-3 w-3 text-muted-foreground" /> O que veio na lista
        </h3>
        {origem && (
          <span className="inline-flex items-center rounded-full border border-border bg-[#F2EEE8] px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-muted-foreground dark:bg-white/[0.06]">{origem}</span>
        )}
      </div>
      <div className="grid gap-x-6 px-3.5 py-2 md:grid-cols-2">
        {pares.map(([rotulo, texto]) => (
          <div key={rotulo} className="flex h-[30px] items-center justify-between gap-3 border-b border-[#F0E9E1] last:border-b-0 dark:border-white/[0.05]">
            <span className="flex-shrink-0 text-[11px] text-muted-foreground">{rotulo}</span>
            <span className="min-w-0 truncate text-right text-xs font-semibold text-[#2E2019] dark:text-[#E8DEDE]" title={texto ?? undefined}>{texto}</span>
          </div>
        ))}
      </div>
    </section>
  );
}
